import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Radio } from 'lucide-react';

const ZigbeeDeviceForm = ({ onSubmit, onCancel, isAdding }) => {
  const { apiRequest } = useAuth();

  const [formData, setFormData] = useState({
    discoveryTimeout: 120,
  });

  const [discoveryStatus, setDiscoveryStatus] = useState({
    isConnected: false,
    permitJoin: false,
    timeRemaining: null,
  });

  const fetchDiscoveryStatus = useCallback(async () => {
    try {
      const { response, data } = await apiRequest('/api/devices/zigbee/discovery/status');
      if (response.ok) {
        setDiscoveryStatus(data.data);
      }
    } catch (error) {
      console.error('Error fetching discovery status:', error);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchDiscoveryStatus();
    const interval = setInterval(fetchDiscoveryStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchDiscoveryStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isConnected = !!discoveryStatus.isConnected;
  const isDiscoveryActive = !!discoveryStatus.permitJoin;

  const timeLabel = useMemo(() => {
    if (!isDiscoveryActive || !discoveryStatus.timeRemaining) return null;
    const m = Math.floor(discoveryStatus.timeRemaining / 60);
    const s = (discoveryStatus.timeRemaining % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, [isDiscoveryActive, discoveryStatus.timeRemaining]);

  const primaryDisabled = isAdding || !isConnected || isDiscoveryActive;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Status indicator */}
      <div className="flex items-center justify-center gap-3 py-4">
        <div className={`
          w-16 h-16 rounded-full flex items-center justify-center
          ${isDiscoveryActive 
            ? 'bg-green-500/15 text-green-600' 
            : isConnected 
              ? 'bg-blue-500/15 text-blue-600' 
              : 'bg-muted text-muted-foreground'}
        `}>
          <Radio className={`w-7 h-7 ${isDiscoveryActive ? 'animate-pulse' : ''}`} />
        </div>
      </div>

      {/* Status text */}
      <div className="text-center space-y-1">
        {isDiscoveryActive ? (
          <>
            <p className="text-base font-medium text-foreground">Discovery Active</p>
            <p className="text-2xl font-semibold tabular-nums text-green-600">{timeLabel}</p>
            <p className="text-xs text-muted-foreground">Put your device in pairing mode</p>
          </>
        ) : isConnected ? (
          <>
            <p className="text-base font-medium text-foreground">Ready to Pair</p>
            <p className="text-xs text-muted-foreground">Start discovery to add a new device</p>
          </>
        ) : (
          <>
            <p className="text-base font-medium text-foreground">Zigbee Offline</p>
            <p className="text-xs text-muted-foreground">Check your Zigbee coordinator</p>
          </>
        )}
      </div>

      {/* Duration selector - only show when not active */}
      {!isDiscoveryActive && isConnected && (
        <div className="flex justify-center gap-2">
          {[60, 120, 180, 240].map((seconds) => (
            <button
              key={seconds}
              type="button"
              onClick={() => setFormData({ discoveryTimeout: seconds })}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium transition-colors
                ${formData.discoveryTimeout === seconds
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'}
              `}
            >
              {seconds / 60}m
            </button>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isAdding}
          className="flex-1 h-12 rounded-2xl"
        >
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={primaryDisabled}
          className="flex-1 h-12 rounded-2xl"
        >
          {isAdding ? 'Starting...' : isDiscoveryActive ? 'Active' : 'Start Discovery'}
        </Button>
      </div>
    </form>
  );
};

export default ZigbeeDeviceForm;
